/**
 * O paciente da clínica na tela (#1024): o CPF como a tela o mostra e recebe, a
 * situação das consultas em palavras, e quando a agenda da organização deixa
 * procurar o paciente.
 *
 * O CPF inteiro só existe enquanto a pessoa digita: depois da busca, a tela
 * mostra só os dois últimos dígitos, que é o que a API devolve.
 */

import type { SituacaoDaConsultaNoSisac } from '@cliqcrm/contracts';

export function soDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

/**
 * O telefone do contato como o SISAC guarda: DDD e número, só dígitos, sem o
 * 55. É o que vai no celular e no WhatsApp do paciente.
 */
export function telefoneParaOSisac(telefone: string | null): string {
  const d = soDigitos(telefone ?? '');
  return d.startsWith('55') && (d.length === 12 || d.length === 13) ? d.slice(2) : d;
}

export function cpfCompleto(texto: string): boolean {
  return soDigitos(texto).length === 11;
}

/** "•••.•••.•••-00": o CPF depois da busca. */
export function cpfProtegido(finalDoCpf: string): string {
  return `•••.•••.•••-${finalDoCpf}`;
}

/** O CPF enquanto a pessoa digita: "123.456.789-01", sem passar de 11 dígitos. */
export function mascararCpf(texto: string): string {
  const d = soDigitos(texto).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export const NOME_DA_SITUACAO: Readonly<Record<SituacaoDaConsultaNoSisac, string>> = {
  agendada: 'Agendada',
  confirmada: 'Confirmada',
  atendida: 'Atendida',
  falta: 'Faltou',
  outra: 'Sem situação',
};

/**
 * A agenda da organização deixa procurar e vincular o paciente: a busca por CPF
 * e a leitura do paciente ligadas no mapa do `/v1/me`.
 */
export function pacienteNoSisacLigado(clinica: Readonly<Record<string, string>>): boolean {
  return clinica['buscar_paciente'] !== undefined && clinica['ler_paciente'] !== undefined;
}

/** "12/03/1980" de "1980-03-12". */
export function diaNaTela(dia: string | null): string | null {
  const m = dia?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}
